import { ApiResult } from "src/api/types";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import { useTranslation } from "src/hooks";
import {
  findChainInfo,
  listMappingTransform,
  toDisplayTime,
} from "src/utils/helper";
import { GetListRes } from "../types/GetListRes";
import {
  RiskType,
  SweepDestStatus,
  WalletType,
} from "@wallet-manager/node-types/dist/src/postgres/const";
import { TableColumnKey, TableRow } from "../types/Table";

interface UseTableRowsProps {
  apiList: ApiResult<GetListRes>;
}

type MapResultToTupleArray = (
  row: GetListRes[],
  omitKeys: string[],
) => any[][][];

const useTableRows = (props: UseTableRowsProps) => {
  const { translatePrefix } = usePageConfigProviderContext();
  const { t, te, translate } = useTranslation(translatePrefix);
  const { apiList } = props;

  const getRiskType = (riskType: number) => {
    return translate(`enumRiskType.${RiskType[riskType]}`);
  };

  const getSweepDestStatus = (status: number) => {
    return translate(`enumAutoSweepDestStatus.${SweepDestStatus[status]}`);
  };

  const getWalletType = (type: number | undefined) => {
    if (type === undefined) return "";
    return te(WalletType[type] as any);
  };

  const mapResultToTupleArray: MapResultToTupleArray = (rows, omitKeys) => {
    return rows.map((item) => {
      const mappedResult = [
        [
          TableColumnKey.ChainName,
          t(TableColumnKey.ChainName),
          findChainInfo(item.chain_type?.toString(), item.chain_id)?.name,
        ],
        [TableColumnKey.Currency, t(TableColumnKey.Currency), item.asset_name],
        [TableColumnKey.Rating, t(TableColumnKey.Rating), item.probability],
        [
          TableColumnKey.GroupName,
          t(TableColumnKey.GroupName),
          item.auto_sweep_asset_dest_group?.group_name,
        ],
        [
          TableColumnKey.WalletDisplayName,
          t(TableColumnKey.WalletDisplayName),
          item.wallet?.display_name,
        ],
        [
          TableColumnKey.RecipientAddress,
          t(TableColumnKey.RecipientAddress),
          item.wallet?.wallet_address,
        ],
        [
          TableColumnKey.WalletType,
          t(TableColumnKey.WalletType),
          getWalletType(item.wallet?.wallet_type),
        ],
        [
          TableColumnKey.RiskType,
          t(TableColumnKey.RiskType),
          getRiskType(item.risk_type),
        ],
        [
          TableColumnKey.Status,
          t(TableColumnKey.Status),
          getSweepDestStatus(item.status),
        ],
        [
          TableColumnKey.CreatedBy,
          t(TableColumnKey.CreatedBy),
          item.updated_by, //created by not exist
        ],
        [
          TableColumnKey.CreationTime,
          t(TableColumnKey.CreationTime),
          toDisplayTime(item.created_date),
        ],
        [
          TableColumnKey.LastModifiedTime,
          t(TableColumnKey.LastModifiedTime),
          toDisplayTime(item.last_modified_date),
        ],
        ["rawData", "", item],
      ].filter(([key]) => !omitKeys.includes(key as string));
      return mappedResult;
    });
  };

  const res = mapResultToTupleArray(apiList.rows, []);
  const rows: TableRow[] = res.map(listMappingTransform("key"));

  return {
    rows,
    totalCounts: apiList.count,
    mapResultToTupleArray,
  };
};

export default useTableRows;
